import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

/**
 * Superadmin session — deliberately separate from the tenant AuthContext.
 * The backend issues a different cookie scoped to /api/platform, so the two
 * sessions can coexist in one browser without either one granting the other's
 * access.
 */
const PlatformAuthContext = createContext(null);

export function PlatformAuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, admin: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/auth/me");
      setState({ loading: false, admin: data.admin });
    } catch {
      setState({ loading: false, admin: null });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/platform/auth/login", { email, password });
      setState({ loading: false, admin: data.admin });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/platform/auth/logout");
    } catch {
      /* ignore */
    }
    setState({ loading: false, admin: null });
  };

  return (
    <PlatformAuthContext.Provider value={{ ...state, login, logout, reload: load }}>
      {children}
    </PlatformAuthContext.Provider>
  );
}

export function usePlatformAuth() {
  return useContext(PlatformAuthContext);
}
